import { SIDEBAR_COUNTS_MAX_AGE_MS, viewsHaveTimeFilters } from '../shared/sidebar-counts.js';

// Sidebar counts may lag; ticket lists, conversations and editors do not use
// this scheduler. Keep the server snapshot's deadline, never restart its age.
export function createSidebarRefresh({ read, apply, active, scope, hidden = () => document.hidden, now = Date.now, setTimer = setTimeout, clearTimer = clearTimeout }) {
  let validUntil = 0, lastEventAt = -Infinity, dirty = false, immediate = false;
  let timer = null, running = null, epoch = 0;

  function arm() {
    clearTimer(timer); timer = null;
    if (!dirty || running || !active() || hidden()) return;
    const wait = immediate ? 0 : validUntil > now() ? validUntil - now() : 250;
    timer = setTimer(() => void run(), wait);
  }

  function accept(value, { timeDependent = false } = {}) {
    const parsed = Date.parse(value);
    validUntil = Number.isFinite(parsed) ? Math.min(parsed, now() + SIDEBAR_COUNTS_MAX_AGE_MS) : now() + SIDEBAR_COUNTS_MAX_AGE_MS;
    // A forced metadata read can still contain counts cached before an event.
    dirty = timeDependent || lastEventAt >= validUntil - SIDEBAR_COUNTS_MAX_AGE_MS;
    arm();
  }

  async function run() {
    timer = null;
    if (running || !active() || hidden()) return;
    const task = { controller: new AbortController(), epoch, scope: scope() };
    const force = immediate;
    running = task; dirty = false; immediate = false;
    const current = () => running === task && task.epoch === epoch && task.scope === scope() && !task.controller.signal.aborted && active() && !hidden();
    try {
      // Metadata changes must not join a GET that started before the change.
      const payload = await read({ signal: task.controller.signal, ...(force ? { cache: 'reload' } : {}) });
      if (!current()) return;
      if (apply(payload) === false) return;
      const views = Array.isArray(payload.data) ? payload.data : payload.data.views || [];
      accept(payload.data.countsValidUntil, { timeDependent: viewsHaveTimeFilters(views) });
    } catch {
      if (current()) { dirty = true; validUntil = now() + 1000; }
    } finally {
      if (running === task) { running = null; arm(); }
    }
  }

  function suspend() {
    epoch++;
    clearTimer(timer); timer = null;
    running?.controller.abort(); running = null;
  }

  return {
    accept,
    refresh({ immediate: force = false } = {}) {
      lastEventAt = now(); dirty = true;
      if (force) { suspend(); immediate = true; }
      arm();
    },
    suspend,
    reset() { suspend(); validUntil = 0; lastEventAt = -Infinity; dirty = false; immediate = false; },
    visibilityChanged() {
      if (hidden()) suspend();
      else this.refresh({ immediate: true });
    }
  };
}
